import React from 'react';
import type {
  ConditionalVisibility,
  FieldShortcut,
  FormSectionsWithShortcuts,
} from 'korint-sdk/react';
import type { ConfigCustomFieldDto } from 'korint-sdk';
import {
  configControllerGetPublicProductConfigQueryOptions,
  policyControllerGetPolicyQueryOptions,
} from 'korint-sdk';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAssertedParams } from '@folio/lib';
import { CustomerFunnelModule } from '../CustomerFunnelModule/CustomerFunnelModule';
import {
  type AfterSubmitFn,
  type CleanupPredicate,
  buildFieldCleanup,
  composeAfterSubmitFns,
} from '../buildFieldCleanup';
import {
  BriefcaseIcon,
  Building2,
  CalendarDays,
  FileText,
  Globe,
  Hash,
  MapPin,
} from 'lucide-react';

const SIREN_INTEGRATION_KEYS = new Set([
  'siren',
  'siret',
  'nafCode',
  'nafLabel',
  'name',
  'creationDate',
  'location.address',
  'location.postCode',
  'location.city',
  'location.country',
  'legalStatus.1',
  'legalStatus.2',
  'legalStatus.3',
]);

const showWhenCompanyBeingCreated = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'true',
  },
] as const satisfies ConditionalVisibility[];

const INTEGRATION_KEY_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  name: Building2,
  siren: Hash,
  nafCode: Hash,
  nafLabel: BriefcaseIcon,
  creationDate: CalendarDays,
  'location.address': MapPin,
  'location.postCode': Hash,
  'location.city': Building2,
  'location.country': Globe,
  'legalStatus.1': FileText,
  'legalStatus.2': FileText,
  'legalStatus.3': FileText,
};

const INTEGRATION_KEY_COL_SPAN: Record<string, 4 | 8> = {
  'location.postCode': 4,
  'location.city': 8,
};

function getManualCompanyFields(customerFields: ConfigCustomFieldDto[]): FieldShortcut[] {
  return customerFields
    .filter(
      field =>
        field.integrationKey &&
        SIREN_INTEGRATION_KEYS.has(field.integrationKey) &&
        !field.isCalculated &&
        field.integrationKey !== 'siret' &&
        field.requiredFor &&
        field.requiredFor !== 'NOTHING' &&
        field.requiredFor !== 'NEVER',
    )
    .map(field => ({
      name: field.key,
      showWhen: showWhenCompanyBeingCreated,
      ...(field.integrationKey && INTEGRATION_KEY_ICON[field.integrationKey]
        ? { icon: INTEGRATION_KEY_ICON[field.integrationKey] }
        : {}),
      ...(field.integrationKey && INTEGRATION_KEY_COL_SPAN[field.integrationKey]
        ? { colSpan: INTEGRATION_KEY_COL_SPAN[field.integrationKey] }
        : {}),
    }));
}

const fieldShortcutKey = (field: FieldShortcut): string =>
  typeof field === 'string' ? field : field.name;

// Company data fields enriched by the SIREN integration (from a picked existing company) that are
// NOT re-entered manually when `isCompanyBeingCreated` is toggled on, and NOT server-calculated
// (nulling a calculated field is rejected with INVALID_CUSTOM_FIELDS). These are the orphans that
// would otherwise persist into the generated CONTRACT document. `formOwnedKeys` are the fields the
// create-mode form actually renders (built-in manual fields + caller `additionalFields`/sections);
// their values are saved by the form, so they must never be nulled.
function getCompanyCleanupKeys(
  customerFields: ConfigCustomFieldDto[],
  formOwnedKeys: Set<string>,
): string[] {
  return customerFields
    .filter(
      field =>
        field.integrationKey &&
        SIREN_INTEGRATION_KEYS.has(field.integrationKey) &&
        !field.isCalculated &&
        !formOwnedKeys.has(field.key),
    )
    .map(field => field.key);
}

// The toggle serializes as a boolean, but showWhen chains compare against 'true'/'false' strings;
// accept both so the cleanup fires regardless of the coercion path.
const isCompanyBeingCreated: CleanupPredicate = data =>
  data.isCompanyBeingCreated === true || data.isCompanyBeingCreated === 'true';

function getSiretApiDataAccessor(customerFields: ConfigCustomFieldDto[]) {
  const findFieldByIntegrationKey = (key: string) =>
    customerFields.find(f => f.integrationKey === key)?.key;

  const postcodeFieldKey = findFieldByIntegrationKey('location.postCode');
  const nameFieldKey = findFieldByIntegrationKey('name');
  const nafLabelFieldKey = findFieldByIntegrationKey('nafLabel');
  const siretFieldKey = findFieldByIntegrationKey('siret');

  // biome-ignore lint/suspicious/noExplicitAny: Customer API data shape is dynamic
  return (apiData: any) =>
    apiData.customFields?.[siretFieldKey!]
      ? {
          siret: apiData.customFields[siretFieldKey!],
          name: nameFieldKey ? apiData.customFields[nameFieldKey] : undefined,
          location: postcodeFieldKey
            ? { postCode: apiData.customFields[postcodeFieldKey] }
            : undefined,
          nafLabel: nafLabelFieldKey ? apiData.customFields[nafLabelFieldKey] : undefined,
        }
      : null;
}

function buildCompanySections({
  customerFields,
  sectionTitle,
  additionalFields,
  additionalSections,
}: {
  customerFields: ConfigCustomFieldDto[];
  sectionTitle: string;
  additionalFields?: FieldShortcut[];
  additionalSections?: FormSectionsWithShortcuts;
}): FormSectionsWithShortcuts {
  const manualFields = getManualCompanyFields(customerFields);
  const siretFieldKey =
    customerFields.find(f => f.integrationKey === 'siret')?.key ?? 'companySiret';

  return [
    {
      title: sectionTitle,
      fields: [
        { name: 'isCompanyBeingCreated', display: 'toggle' as const },
        {
          name: siretFieldKey,
          type: 'company',
          required: true,
          isCustomField: true,
          displayCompanyInformation: true,
          apiDataAccessor: getSiretApiDataAccessor(customerFields),
          formDataAccessor: `${siretFieldKey}.siret`,
          showWhen: [
            {
              fieldName: 'isCompanyBeingCreated',
              condition: 'equals',
              value: 'false',
            },
          ],
        },
        ...manualFields,
        ...(additionalFields ?? []),
      ],
    },
    ...(additionalSections ?? []),
  ];
}

type CompanyFunnelModuleProps = {
  additionalFields?: FieldShortcut[];
  additionalSections?: FormSectionsWithShortcuts;
  afterSubmit?: AfterSubmitFn;
  title?: string;
  subtitle?: string;
};

export function CompanyFunnelModule({
  additionalFields,
  additionalSections,
  afterSubmit,
  title,
  subtitle,
}: CompanyFunnelModuleProps) {
  const { t } = useTranslation();
  const { policyId, branchId } = useAssertedParams();

  const { data: productId } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    select: data => data.productId,
  });

  const { data: customerFields = [] } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId!),
    enabled: Boolean(productId),
    select: data => data.customerFields,
  });

  const sections = buildCompanySections({
    customerFields,
    sectionTitle: t('companyFunnel.title'),
    additionalFields,
    additionalSections,
  });

  const formOwnedKeys = new Set([
    ...getManualCompanyFields(customerFields).map(fieldShortcutKey),
    ...(additionalFields ?? []).map(fieldShortcutKey),
    ...(additionalSections ?? []).flatMap(section => section.fields.map(fieldShortcutKey)),
  ]);
  const cleanupOrphanCompanyFields: AfterSubmitFn = buildFieldCleanup(
    isCompanyBeingCreated,
    getCompanyCleanupKeys(customerFields, formOwnedKeys),
  );
  const composedAfterSubmit = afterSubmit
    ? composeAfterSubmitFns(cleanupOrphanCompanyFields, afterSubmit)
    : cleanupOrphanCompanyFields;

  return (
    <CustomerFunnelModule
      sections={sections}
      path="company"
      afterSubmit={composedAfterSubmit}
      title={title ?? t('funnelSteps.company.title')}
      subtitle={subtitle ?? t('funnelSteps.company.subtitle', '')}
    />
  );
}
